"use client";

import { useState, useEffect, useCallback } from "react";
import { Link2, Check, MessageCircle, Share } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { copyToClipboard } from "@/lib/utils/clipboard";

// Kakao SDK type declarations
declare global {
  interface Window {
    Kakao?: {
      init: (key: string) => void;
      isInitialized: () => boolean;
      Share: {
        sendDefault: (options: KakaoShareOptions) => void;
      };
    };
  }
}

interface KakaoShareOptions {
  objectType: "feed";
  content: {
    title: string;
    description: string;
    imageUrl: string;
    link: {
      mobileWebUrl: string;
      webUrl: string;
    };
  };
  buttons?: Array<{
    title: string;
    link: {
      mobileWebUrl: string;
      webUrl: string;
    };
  }>;
}

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    id: string;
    name: string;
    description?: string | null;
    images?: { image_url: string }[];
  };
  locale: string;
}

// X (Twitter) Icon
const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Facebook Icon
const FacebookIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Kakao Icon
const KakaoIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 3c-5.52 0-10 3.59-10 8 0 2.84 1.89 5.33 4.72 6.73-.19.7-.68 2.53-.78 2.92-.12.49.18.48.38.35.16-.1 2.53-1.72 3.56-2.42.69.1 1.4.15 2.12.15 5.52 0 10-3.59 10-8s-4.48-8-10-8z" />
  </svg>
);

export default function ShareDialog({
  open,
  onOpenChange,
  product,
  locale,
}: ShareDialogProps) {
  const t = useTranslations("product.share");
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  // Share URL
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/${locale}/products/${product.id}`
      : "";

  // Check for Web Share API support
  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && !!navigator.share);
  }, []);

  // Load Kakao SDK
  useEffect(() => {
    if (typeof window === "undefined") return;

    const kakaoKey = process.env.NEXT_PUBLIC_KAKAO_JS_KEY;
    if (!kakaoKey) return;

    if (!window.Kakao) {
      const script = document.createElement("script");
      script.src = "https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js";
      script.async = true;
      script.onload = () => {
        if (window.Kakao && !window.Kakao.isInitialized()) {
          window.Kakao.init(kakaoKey);
        }
      };
      document.head.appendChild(script);
    } else if (!window.Kakao.isInitialized()) {
      window.Kakao.init(kakaoKey);
    }
  }, []);

  // Handle copy link
  const handleCopyLink = useCallback(async () => {
    const ok = await copyToClipboard(shareUrl);
    if (!ok) {
      toast.error("Failed to copy");
      return;
    }
    setCopied(true);
    toast.success(t("linkCopied"));
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl, t]);

  // Handle Kakao share
  const handleKakaoShare = useCallback(() => {
    if (!window.Kakao?.isInitialized()) {
      toast.error("Kakao SDK not initialized");
      return;
    }

    window.Kakao.Share.sendDefault({
      objectType: "feed",
      content: {
        title: product.name,
        description: product.description || "",
        imageUrl: product.images?.[0]?.image_url || "",
        link: {
          mobileWebUrl: shareUrl,
          webUrl: shareUrl,
        },
      },
      buttons: [
        {
          title: t("viewProduct"),
          link: {
            mobileWebUrl: shareUrl,
            webUrl: shareUrl,
          },
        },
      ],
    });
  }, [product, shareUrl, t]);

  // Handle X (Twitter) share
  const handleTwitterShare = useCallback(() => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(product.name);
    window.open(
      `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [product.name, shareUrl]);

  // Handle Facebook share
  const handleFacebookShare = useCallback(() => {
    const encodedUrl = encodeURIComponent(shareUrl);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [shareUrl]);

  // Handle native share (Web Share API)
  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) return;

    try {
      await navigator.share({
        title: product.name,
        text: product.description || "",
        url: shareUrl,
      });
    } catch (err) {
      // User cancelled - ignore
      if ((err as Error).name !== "AbortError") {
        console.error("Share failed:", err);
      }
    }
  }, [product, shareUrl]);

  // Share button component
  const ShareButton = ({
    onClick,
    icon,
    label,
    className,
  }: {
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    className?: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl transition-all",
        "hover:bg-gray-100 active:scale-95",
        className
      )}
    >
      <div className="w-11 h-11 rounded-full flex items-center justify-center">
        {icon}
      </div>
      <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">{label}</span>
    </button>
  );

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("title")}
    >
      <div className={cn(
        "grid gap-2 justify-items-center items-start",
        canNativeShare ? "grid-cols-5" : "grid-cols-4"
      )}>
        {/* Copy Link */}
        <ShareButton
          onClick={handleCopyLink}
          icon={
            <div
              className={cn(
                "w-11 h-11 rounded-full flex items-center justify-center transition-colors",
                copied ? "bg-green-100" : "bg-gray-100"
              )}
            >
              {copied ? (
                <Check className="w-6 h-6 text-green-600" />
              ) : (
                <Link2 className="w-6 h-6 text-gray-700" />
              )}
            </div>
          }
          label={copied ? t("copied") : t("copyLink")}
        />

        {/* Kakao */}
        <ShareButton
          onClick={handleKakaoShare}
          icon={
            <div className="w-11 h-11 rounded-full bg-[#FEE500] flex items-center justify-center">
              <KakaoIcon className="w-6 h-6 text-[#191919]" />
            </div>
          }
          label={t("kakao")}
        />

        {/* X (Twitter) */}
        <ShareButton
          onClick={handleTwitterShare}
          icon={
            <div className="w-11 h-11 rounded-full bg-black flex items-center justify-center">
              <XIcon className="w-5 h-5 text-white" />
            </div>
          }
          label={t("twitter")}
        />

        {/* Facebook */}
        <ShareButton
          onClick={handleFacebookShare}
          icon={
            <div className="w-11 h-11 rounded-full bg-[#1877F2] flex items-center justify-center">
              <FacebookIcon className="w-6 h-6 text-white" />
            </div>
          }
          label={t("facebook")}
        />

        {/* Native Share (Mobile/PWA only) */}
        {canNativeShare && (
          <ShareButton
            onClick={handleNativeShare}
            icon={
              <div className="w-11 h-11 rounded-full bg-orange-500 flex items-center justify-center">
                <Share className="w-6 h-6 text-white" />
              </div>
            }
            label={t("more")}
          />
        )}
      </div>
    </ResponsiveModal>
  );
}
